// Reglerzuordnung für eigene Plugins (Bridge): welche Regler eines fremden Plugins welchem Auto-Mix-Wert entsprechen.
//
// Die Bridge meldet je Regler den Namen und die Anzeige an 33 gleichmäßig verteilten Stellen ("−60.0 dB" … "0.0 dB").
// Daraus wird hier
//   - automatisch zugeordnet (nach Namen, bei EQs nach Bändern und Filtertypen),
//   - aus einem gewünschten Wert (z. B. −22 dB) die Reglerstellung 0..1 bestimmt (Interpolation, logarithmisch, wo es passt),
//   - die Zuordnung pro Plugin gespeichert; der Mensch kann sie im Auto-Mix prüfen und ändern.
import type { InsertSlot, PluginParamInfo } from '@srs/shared';

export type Task = 'comp' | 'eq' | 'deesser' | 'reverb' | 'saturation' | 'gain' | 'delay';
export type Unit = 'dB' | 'ms' | 's' | 'Hz' | 'ratio' | '%' | 'q' | 'rel';

export interface SemKey { key: string; label: string; unit: Unit; rx: RegExp }

export const TASK_LABEL: Record<Task, string> = { comp: 'Kompressor', eq: 'EQ', deesser: 'De-Esser', reverb: 'Hall', saturation: 'Sättigung', gain: 'Pegel (Utility)', delay: 'Delay' };

const EQ_BANDS = [
  { id: 'hp', label: 'Low-Cut', shape: /(low|lo)\s*-?\s*cut|high\s*-?\s*pass|\bhpf?\b|\blc\b/i },
  { id: 'ls', label: 'Low-Shelf', shape: /low\s*-?\s*shelf|\blsf?\b|bass\s*shelf/i },
  { id: 'b1', label: 'Band 1', shape: /bell|peak|parametric/i },
  { id: 'b2', label: 'Band 2', shape: /bell|peak|parametric/i },
  { id: 'b3', label: 'Band 3', shape: /bell|peak|parametric/i },
  { id: 'hs', label: 'High-Shelf', shape: /high\s*-?\s*shelf|hi\s*-?\s*shelf|\bhsf?\b|treble/i },
] as const;

/** Werte, die der Auto-Mix je Aufgabe setzt */
export const TASK_KEYS: Record<Task, SemKey[]> = {
  comp: [
    { key: 'thr', label: 'Threshold', unit: 'dB', rx: /thresh|\bthr\b|schwelle/i },
    { key: 'ratio', label: 'Ratio', unit: 'ratio', rx: /ratio/i },
    { key: 'atk', label: 'Attack', unit: 'ms', rx: /attack|\batk\b/i },
    { key: 'rel', label: 'Release', unit: 'ms', rx: /release|\brel\b/i },
    { key: 'makeup', label: 'Make-up', unit: 'dB', rx: /make\s*-?\s*up|^output( gain)?$|^out(put)?$|^gain$/i },
    { key: 'mix', label: 'Mix', unit: '%', rx: /\bmix\b|dry\s*\/?\s*wet|blend|parallel/i },
  ],
  eq: [
    { key: 'hpF', label: 'Low-Cut Frequenz', unit: 'Hz', rx: /^$/ },
    { key: 'lsF', label: 'Low-Shelf Frequenz', unit: 'Hz', rx: /^$/ }, { key: 'lsG', label: 'Low-Shelf Gain', unit: 'dB', rx: /^$/ },
    { key: 'b1F', label: 'Band 1 Frequenz', unit: 'Hz', rx: /^$/ }, { key: 'b1G', label: 'Band 1 Gain', unit: 'dB', rx: /^$/ }, { key: 'b1Q', label: 'Band 1 Q', unit: 'q', rx: /^$/ },
    { key: 'b2F', label: 'Band 2 Frequenz', unit: 'Hz', rx: /^$/ }, { key: 'b2G', label: 'Band 2 Gain', unit: 'dB', rx: /^$/ }, { key: 'b2Q', label: 'Band 2 Q', unit: 'q', rx: /^$/ },
    { key: 'b3F', label: 'Band 3 Frequenz', unit: 'Hz', rx: /^$/ }, { key: 'b3G', label: 'Band 3 Gain', unit: 'dB', rx: /^$/ }, { key: 'b3Q', label: 'Band 3 Q', unit: 'q', rx: /^$/ },
    { key: 'hsF', label: 'High-Shelf Frequenz', unit: 'Hz', rx: /^$/ }, { key: 'hsG', label: 'High-Shelf Gain', unit: 'dB', rx: /^$/ },
  ],
  deesser: [
    { key: 'freq', label: 'Frequenz', unit: 'Hz', rx: /freq/i },
    { key: 'thr', label: 'Threshold', unit: 'dB', rx: /thresh|\bthr\b/i },
    { key: 'range', label: 'Range', unit: 'dB', rx: /range|reduction|depth|\bmax\b/i },
  ],
  reverb: [
    { key: 'decay', label: 'Decay', unit: 's', rx: /decay|reverb\s*time|\brt\s*-?60\b|^time$|length/i },
    { key: 'pre', label: 'Pre-Delay', unit: 'ms', rx: /pre\s*-?\s*delay|predelay/i },
    { key: 'mix', label: 'Mix', unit: '%', rx: /\bmix\b|dry\s*\/?\s*wet|\bwet\b/i },
  ],
  saturation: [
    { key: 'drive', label: 'Drive', unit: 'rel', rx: /drive|saturat|amount|heat|input/i },
    { key: 'mix', label: 'Mix', unit: '%', rx: /\bmix\b|dry\s*\/?\s*wet|blend/i },
  ],
  gain: [{ key: 'gain', label: 'Gain', unit: 'dB', rx: /gain|trim|level|volume|output/i }],
  delay: [
    { key: 'time', label: 'Zeit', unit: 'ms', rx: /time|delay/i },
    { key: 'feedback', label: 'Feedback', unit: '%', rx: /feedback|\bfb\b|repeats/i },
    { key: 'mix', label: 'Mix', unit: '%', rx: /\bmix\b|dry\s*\/?\s*wet|blend/i },
  ],
};

export interface MapEntry { i: number; kind: 'num' | 'enum'; want?: string; rel?: boolean }
export interface PluginProfile { pluginId: string; task: Task; map: Record<string, MapEntry>; confirmed: boolean }

// ---------------------------------------------------------------------------
// Anzeigetexte lesen

/** Zahl aus einer Anzeige ("−18.0 dB", "4.0:1", "2.5 kHz", "120 ms", "1.80 s") in der gewünschten Einheit */
export function parseValue(text: string, unit: Unit): number | null {
  const t = text.replace(/[−–]/g, '-').replace(/(\d),(\d)/g, '$1.$2').trim();
  if (unit === 'ratio' && /∞|inf/i.test(t)) return 100;
  if (/^(off|aus|-?inf|-?∞)$/i.test(t)) return unit === 'dB' ? -150 : null;
  const m = t.match(/[-+]?\d*\.?\d+(e[-+]?\d+)?/i);
  if (!m || m.index === undefined) return null;
  let v = parseFloat(m[0]);
  const rest = t.slice(m.index + m[0].length).trim().toLowerCase();
  if (unit === 'Hz' && /^k/.test(rest)) v *= 1000;
  if (unit === 'ms' && /^(s|sec)\b/.test(rest)) v *= 1000;
  if (unit === 's' && /^ms/.test(rest)) v /= 1000;
  if (unit === 'ratio' && /^:\s*1/.test(rest) === false && /^1\s*:/.test(t)) {
    // "1:4" statt "4:1"
    const n = t.match(/1\s*:\s*(\d*\.?\d+)/);
    if (n) v = parseFloat(n[1]);
  }
  return Number.isFinite(v) ? v : null;
}

/** Reglerstellung 0..1 für einen gewünschten Wert. null = Regler hat keine lesbaren Zahlen. */
export function normFor(texts: string[], unit: Unit, target: number): number | null {
  if (unit === 'rel') return Math.max(0, Math.min(1, target));
  const n = texts.length;
  const pts = texts.map((t, k) => ({ x: k / (n - 1), v: parseValue(t, unit) })).filter((p): p is { x: number; v: number } => p.v !== null);
  if (pts.length < 2) return null;
  let best: number | null = null, bestD = Infinity;
  for (let k = 0; k + 1 < pts.length; k++) {
    const a = pts[k], b = pts[k + 1];
    const lo = Math.min(a.v, b.v), hi = Math.max(a.v, b.v);
    if (target >= lo && target <= hi) {
      if (a.v === b.v) return a.x;
      const useLog = a.v > 0 && b.v > 0 && hi / lo > 1.5;
      const f = useLog ? Math.log(target / a.v) / Math.log(b.v / a.v) : (target - a.v) / (b.v - a.v);
      return a.x + f * (b.x - a.x);
    }
    for (const p of [a, b]) { const d = Math.abs(p.v - target); if (d < bestD) { bestD = d; best = p.x; } }
  }
  return best;   // außerhalb des Bereichs: nächstes Ende
}

/** Stellung eines Auswahl-Reglers, dessen Anzeige auf das Muster passt (Mitte des ersten Treffers) */
export function enumNorm(texts: string[], want: RegExp): number | null {
  const n = texts.length;
  let a = -1, b = -1;
  for (let k = 0; k < n; k++) {
    if (want.test(texts[k])) { if (a < 0) a = k; b = k; }
    else if (a >= 0) break;
  }
  return a < 0 ? null : ((a + b) / 2) / (n - 1);
}

// ---------------------------------------------------------------------------
// Automatische Zuordnung

const numeric = (p: PluginParamInfo, unit: Unit) => unit === 'rel' || p.texts.filter((t) => parseValue(t, unit) !== null).length >= Math.min(3, p.texts.length);

export function autoProfile(pluginId: string, task: Task, params: PluginParamInfo[]): PluginProfile {
  const map: Record<string, MapEntry> = {};
  const usable = params.filter((p) => p.auto);
  if (task === 'eq') {
    // Bänder: "Band 1 Frequency", "B2 Gain", "Band 3 Q" …; sonst feste Namen (Low Cut, Low Shelf, High Shelf)
    const bands = new Map<number, PluginParamInfo[]>();
    for (const p of usable) {
      const m = p.name.match(/\b(?:band|b)\s*0?(\d{1,2})\b/i);
      if (m) { const k = Number(m[1]); bands.set(k, [...(bands.get(k) ?? []), p]); }
    }
    const order = [...bands.keys()].sort((a, b) => a - b);
    const field = (list: PluginParamInfo[], rx: RegExp) => list.find((p) => rx.test(p.name.replace(/\b(?:band|b)\s*0?\d{1,2}\b/i, '')));
    if (order.length >= 3) {
      EQ_BANDS.forEach((band, idx) => {
        const list = bands.get(order[idx]);
        if (!list) return;
        const f = field(list, /freq|frequency|\bf\b/i), g = field(list, /gain|\bg\b/i), q = field(list, /\bq\b|width|bandwidth|reso/i);
        const shape = field(list, /shape|type|mode|filter/i), on = field(list, /enable|\bon\b|active|used|bypass|\bpower\b/i);
        if (f) map[`${band.id}F`] = { i: f.i, kind: 'num' };
        if (g && band.id !== 'hp') map[`${band.id}G`] = { i: g.i, kind: 'num' };
        if (q && band.id.startsWith('b')) map[`${band.id}Q`] = { i: q.i, kind: 'num' };
        if (shape && enumNorm(shape.texts, band.shape) !== null) map[`${band.id}.shape`] = { i: shape.i, kind: 'enum', want: band.shape.source };
        if (on) {
          const bypass = /bypass/i.test(on.name);
          const want = bypass ? /off|no|false|aus|^0$/i : /^(on|yes|true|an|enabled|active|1)$/i;
          if (enumNorm(on.texts, want) !== null) map[`${band.id}.on`] = { i: on.i, kind: 'enum', want: want.source };
        }
      });
    } else {
      const find = (rx: RegExp) => usable.find((p) => rx.test(p.name));
      const hp = find(/(low|lo)\s*-?\s*cut.*freq|high\s*-?\s*pass.*freq|\bhpf?\b.*freq|^(low\s*cut|hpf?|high\s*pass)$/i);
      const lsF = find(/(low|lo|lf)\b.*(shelf)?.*freq/i), lsG = find(/(low|lo|lf)\b.*gain/i);
      const hsF = find(/(high|hi|hf)\b.*(shelf)?.*freq/i), hsG = find(/(high|hi|hf)\b.*gain/i);
      const mF = find(/(mid|lmf|mf)\b.*freq/i), mG = find(/(mid|lmf|mf)\b.*gain/i), mQ = find(/(mid|lmf|mf)\b.*(\bq\b|width)/i);
      if (hp) map.hpF = { i: hp.i, kind: 'num' };
      if (lsF) map.lsF = { i: lsF.i, kind: 'num' };
      if (lsG) map.lsG = { i: lsG.i, kind: 'num' };
      if (hsF) map.hsF = { i: hsF.i, kind: 'num' };
      if (hsG) map.hsG = { i: hsG.i, kind: 'num' };
      if (mF) map.b2F = { i: mF.i, kind: 'num' };
      if (mG) map.b2G = { i: mG.i, kind: 'num' };
      if (mQ) map.b2Q = { i: mQ.i, kind: 'num' };
    }
    return { pluginId, task, map, confirmed: false };
  }
  const taken = new Set<number>();
  for (const k of TASK_KEYS[task]) {
    const p = usable.find((x) => !taken.has(x.i) && k.rx.test(x.name) && numeric(x, k.unit));
    if (p) { map[k.key] = { i: p.i, kind: 'num', ...(k.unit === 'rel' ? { rel: true } : {}) }; taken.add(p.i); }
  }
  return { pluginId, task, map, confirmed: false };
}

const unitOf = (task: Task, key: string): Unit => {
  const base = key.replace(/\..*$/, '');
  return TASK_KEYS[task].find((k) => k.key === base)?.unit ?? 'rel';
};

/**
 * Auto-Mix-Werte in ein eigenes Plugin schreiben. Gibt die Werte zurück, die sich nicht zuordnen ließen
 * (als lesbare Namen, z. B. "Ratio"). EQ-Bänder werden mit Filtertyp und Einschalter gesetzt, wenn bekannt.
 */
export function writeSem(slot: InsertSlot, params: PluginParamInfo[], profile: PluginProfile, sem: Record<string, number>): string[] {
  const miss: string[] = [];
  const byI = new Map(params.map((p) => [p.i, p]));
  const set = (i: number, v: number) => { slot.params[`p${i}`] = Math.max(0, Math.min(1, v)); if (slot.texts) delete slot.texts[`p${i}`]; };
  for (const [key, value] of Object.entries(sem)) {
    if (key.endsWith('On')) continue;   // Einschalter der eingebauten EQ-Bänder: über ".on" unten
    const e = profile.map[key];
    const par = e && byI.get(e.i);
    const label = TASK_KEYS[profile.task].find((k) => k.key === key)?.label ?? key;
    if (!e || !par) { miss.push(label); continue; }
    const v = e.rel ? value : normFor(par.texts, unitOf(profile.task, key), value);
    if (v === null) { miss.push(label); continue; }
    set(e.i, v);
    // EQ: Band einschalten und Filtertyp setzen
    const band = key.match(/^(hp|ls|hs|b\d)/)?.[1];
    if (band) for (const extra of ['shape', 'on']) {
      const x = profile.map[`${band}.${extra}`], px = x && byI.get(x.i);
      if (x?.want && px) { const nv = enumNorm(px.texts, new RegExp(x.want, 'i')); if (nv !== null) set(x.i, nv); }
    }
  }
  return [...new Set(miss)];
}

/** Wie vollständig ist eine Zuordnung? (zugeordnet, gebraucht) */
export function coverage(profile: PluginProfile): { mapped: number; total: number } {
  const keys = TASK_KEYS[profile.task].map((k) => k.key);
  return { mapped: keys.filter((k) => profile.map[k]).length, total: keys.length };
}

// ---------------------------------------------------------------------------
// Speichern (je Rechner; die Zuordnung gilt für alle Songs)
const PKEY = 'srs.pluginProfiles';
export function loadProfiles(): Record<string, PluginProfile> {
  try { return JSON.parse(localStorage.getItem(PKEY) ?? '{}') as Record<string, PluginProfile>; } catch { return {}; }
}
export function saveProfile(p: PluginProfile) {
  const all = loadProfiles();
  all[`${p.pluginId}|${p.task}`] = p;
  try { localStorage.setItem(PKEY, JSON.stringify(all)); } catch { /* voll */ }
}
export function profileFor(pluginId: string, task: Task, params?: PluginParamInfo[]): PluginProfile | null {
  const have = loadProfiles()[`${pluginId}|${task}`];
  if (have) return have;
  return params ? autoProfile(pluginId, task, params) : null;
}

/** Lieblings-Plugins je Aufgabe, optional je Spurrolle ("comp:vox") */
export type FxPrefs = Record<string, string>;
const FKEY = 'srs.automix.plugins';
export function loadPrefs(): FxPrefs { try { return JSON.parse(localStorage.getItem(FKEY) ?? '{}') as FxPrefs; } catch { return {}; } }
export function savePrefs(p: FxPrefs) { try { localStorage.setItem(FKEY, JSON.stringify(p)); } catch { /* voll */ } }
/** Plugin für eine Aufgabe: erst je Spurrolle, dann allgemein */
export const prefFor = (prefs: FxPrefs, task: Task, role?: string) => (role && prefs[`${task}:${role}`]) || prefs[task] || '';
